/**
 * Strip whitespace and hyphens anywhere in the string and uppercase it. Does not validate against
 * the invite alphabet or length — that's the server's job (redeem_invite). Shared by
 * `parseJoinCode` (the `?code=` URL path) and `JoinScreen`'s manual-entry path, so a code someone
 * pastes or types by hand is normalized exactly the same way as one that arrives in a link.
 */
export function normalizeJoinCode(raw: string): string {
  return raw.replace(/[\s-]/g, '').toUpperCase()
}

/**
 * Parse and normalize a join code from URL search parameters.
 *
 * Normalization rules (in order):
 * 1. Read the `code` parameter from searchParams. If absent, return null.
 * 2. Strip every whitespace character and every hyphen, anywhere in the string.
 * 3. Uppercase the result.
 * 4. If the result is empty, return null (covers empty parameter and whitespace/hyphen-only values).
 * 5. Otherwise return the normalized string.
 *
 * This function does NOT validate the code against the invite alphabet or length.
 * Validation is the server's responsibility (redeem_invite).
 *
 * @param searchParams - URLSearchParams from the URL (e.g., from `new URL(location.href).searchParams`)
 * @returns The normalized join code, or null if there is no usable code.
 */
export function parseJoinCode(searchParams: URLSearchParams): string | null {
  // Rule 1: Read the code parameter
  const code = searchParams.get('code')
  if (code === null) {
    return null
  }

  // Rules 2 and 3: strip every whitespace character and every hyphen, then uppercase —
  // delegated to `normalizeJoinCode` above so the manual-entry path in `JoinScreen` applies
  // exactly these rules rather than a second copy of them that can drift.
  const normalized = normalizeJoinCode(code)

  // Rule 4: If empty after normalization, return null
  if (normalized === '') {
    return null
  }

  // Rule 5: Return the normalized string
  return normalized
}
